/**
 * buildLaunchMetadata — the `metadata` half of a launch payload.
 *
 * Extracted verbatim from the add-in's `InstanceDetailDrawer.handleLaunch`:
 * `client_context` (client sk/name + policy number + the picked term's
 * dates), `email` (the feedback-loop anchor), and any host-supplied extras
 * (the add-in's own use is `concurrent_run_override` when the operator
 * acknowledges an in-flight run, 1606–1617 — generalised here to `extras`
 * rather than a named param, since a host-specific override belongs to the
 * host, not the core).
 *
 * Returns `undefined`, never `{}`, when nothing populates it — pins today's
 * wire shape. `triggerWorkflow(workflowId, inputs, metadata)` only sets
 * `body.metadata` when its third argument is truthy
 * (`orchestratorApi.ts` 1675: `if (metadata) body.metadata = metadata;`), so
 * an empty object would still ADD the key to the wire body where `undefined`
 * omits it — a distinction `handleRetry` also depends on (it sends NO
 * metadata argument at all, 1822–1825).
 *
 * Pure: no React, no Office.js, no network.
 */
import type { PolicyInForce } from "~/common/workflows/types/client";

export interface LaunchMetadataClient {
  sk?: number | null;
  name?: string | null;
}

export interface BuildLaunchMetadataArgs {
  /** Resolved client identity. The host resolves the display-name fallback
   *  (`clientNameProp ?? next.client_name`, 1567–1568) before calling — this
   *  module only reads what it is handed. */
  client?: LaunchMetadataClient | null;
  /** From the MERGED read view (`resolveLaunchTerm(...).flat.policy_number`),
   *  never the raw run-level `next` — after normalisation the policy number
   *  lives on the action, and reading `next` here would leave
   *  `metadata.client_context.policy` off every reshaped launch. */
  policyNumber?: string | null;
  /** `resolveLaunchTerm(...).chosenTerm`. */
  chosenTerm?: PolicyInForce | null;
  /** The analyzed email's RFC 5322 Message-ID (feedback-loop anchor). */
  internetMessageId?: string | null;
  /** Host-specific additional metadata keys (e.g. the add-in's
   *  `concurrent_run_override: { acknowledged_instance_id }`). Keys whose
   *  value is `undefined` are skipped rather than written as `undefined`. */
  extras?: Record<string, unknown>;
}

export function buildLaunchMetadata({
  client,
  policyNumber,
  chosenTerm,
  internetMessageId,
  extras,
}: BuildLaunchMetadataArgs): Record<string, unknown> | undefined {
  const clientEntry: Record<string, unknown> = {};
  if (client?.sk != null) clientEntry.sk = client.sk;
  if (client?.name) clientEntry.name = client.name;

  const clientContext: Record<string, unknown> = {};
  if (Object.keys(clientEntry).length > 0) clientContext.client = clientEntry;
  if (policyNumber) {
    // Record the chosen TERM alongside the number (additive keys) — a policy
    // with a current term and a future renewal shares one number, so the
    // number alone can't say which term a run endorsed.
    clientContext.policy = {
      number: policyNumber,
      ...(chosenTerm?.effective_date
        ? { term_effective_date: chosenTerm.effective_date }
        : {}),
      ...(chosenTerm?.expiration_date
        ? { term_expiration_date: chosenTerm.expiration_date }
        : {}),
    };
  }

  const metadataObj: Record<string, unknown> = {};
  if (Object.keys(clientContext).length > 0) metadataObj.client_context = clientContext;
  if (internetMessageId) {
    metadataObj.email = { internet_message_id: internetMessageId };
  }
  if (extras) {
    for (const [key, value] of Object.entries(extras)) {
      if (value !== undefined) metadataObj[key] = value;
    }
  }

  return Object.keys(metadataObj).length > 0 ? metadataObj : undefined;
}
